import React, { useState } from 'react';
import styled from 'styled-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { theme } from '../../styles/theme';
import Modal from '../common/Modal';
import Input from '../common/Input';
import Button from '../common/Button';
import { createUser } from '../../api/admin';

const CreateUserModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    isActive: true,
  });
  
  const [errors, setErrors] = useState({});
  const [generatedPassword, setGeneratedPassword] = useState('');
  const queryClient = useQueryClient();
  
  // Create user mutation
  const createMutation = useMutation({
    mutationFn: createUser,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setGeneratedPassword(data.generatedPassword);
      toast.success('Benutzer erfolgreich erstellt');
    },
  });
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };
  
  const validate = () => {
    const newErrors = {};
    
    if (!formData.username.trim()) {
      newErrors.username = 'Benutzername wird benötigt';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'E-Mail wird benötigt';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Gültige E-Mail-Adresse erforderlich';
    }
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Vorname wird benötigt';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Nachname wird benötigt';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    createMutation.mutate(formData);
  };
  
  const handleClose = () => {
    if (createMutation.isPending) {
      return;
    }
    
    setFormData({
      username: '',
      email: '',
      firstName: '',
      lastName: '',
      isActive: true,
    });
    setErrors({});
    setGeneratedPassword('');
    onClose();
  };
  
  const handleCopyPassword = () => {
    navigator.clipboard.writeText(generatedPassword);
    toast.success('Passwort in die Zwischenablage kopiert');
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Neuen Benutzer erstellen"
      closeOnOverlayClick={!createMutation.isPending && !generatedPassword}
      showCloseButton={!createMutation.isPending}
    >
      {generatedPassword ? (
        <SuccessContainer>
          <SuccessMessage>
            Benutzer wurde erfolgreich erstellt. Bitte kopieren Sie das generierte Passwort und teilen Sie es dem Benutzer mit.
          </SuccessMessage>
          
          <PasswordContainer>
            <PasswordLabel>Generiertes Passwort:</PasswordLabel>
            <PasswordValue>{generatedPassword}</PasswordValue>
            <CopyButton onClick={handleCopyPassword}>
              Passwort kopieren
            </CopyButton>
          </PasswordContainer>
          
          <ButtonContainer>
            <Button onClick={handleClose}>Schließen</Button>
          </ButtonContainer>
        </SuccessContainer>
      ) : (
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Input
              id="username"
              name="username"
              label="Benutzername"
              value={formData.username}
              onChange={handleChange}
              error={errors.username}
              disabled={createMutation.isPending}
              fullWidth
            />
          </FormGroup>
          
          <FormGroup>
            <Input
              id="email"
              name="email"
              label="E-Mail"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
              disabled={createMutation.isPending}
              fullWidth
            />
          </FormGroup>
          
          <FormRow>
            <FormGroup>
              <Input
                id="firstName"
                name="firstName"
                label="Vorname"
                value={formData.firstName}
                onChange={handleChange}
                error={errors.firstName}
                disabled={createMutation.isPending}
                fullWidth
              />
            </FormGroup>
            
            <FormGroup>
              <Input
                id="lastName"
                name="lastName"
                label="Nachname"
                value={formData.lastName}
                onChange={handleChange}
                error={errors.lastName}
                disabled={createMutation.isPending}
                fullWidth
              />
            </FormGroup>
          </FormRow>
          
          <FormGroup>
            <CheckboxLabel>
              <CheckboxInput
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                disabled={createMutation.isPending}
              />
              <span>Benutzer aktiv</span>
            </CheckboxLabel>
          </FormGroup>
          
          <ButtonContainer>
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={createMutation.isPending}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              isLoading={createMutation.isPending}
            >
              Benutzer erstellen
            </Button>
          </ButtonContainer>
        </Form>
      )}
    </Modal>
  );
};

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  width: 100%;
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  gap: 0.5rem;
  cursor: pointer;
`;

const CheckboxInput = styled.input`
  width: 1rem;
  height: 1rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SuccessMessage = styled.p`
  color: ${theme.colors.success};
  font-size: 0.875rem;
  background-color: ${theme.colors.success}10;
  padding: 0.75rem;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.success}20;
`;

const PasswordContainer = styled.div`
  background-color: ${theme.colors.backgroundAlt};
  padding: 1rem;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border};
`;

const PasswordLabel = styled.div`
  font-weight: 500;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
`;

const PasswordValue = styled.div`
  font-family: monospace;
  background-color: ${theme.colors.background};
  padding: 0.75rem;
  border-radius: ${theme.borderRadius.sm};
  border: 1px solid ${theme.colors.border};
  margin-bottom: 0.75rem;
  word-break: break-all;
`;

const CopyButton = styled.button`
  padding: 0.5rem 0.75rem;
  background-color: ${theme.colors.backgroundAlt};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: 0.875rem;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  
  &:hover {
    background-color: ${theme.colors.background};
    border-color: ${theme.colors.borderDark};
  }
`;

export default CreateUserModal;
